import { LocalShippingOutlined, PhoneOutlined } from '@mui/icons-material';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import DirectionsCarFilledOutlinedIcon from '@mui/icons-material/DirectionsCarFilledOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PermContactCalendarOutlinedIcon from '@mui/icons-material/PermContactCalendarOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import RestaurantMenuOutlinedIcon from '@mui/icons-material/RestaurantMenuOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { SvgIconTypeMap } from '@mui/material/SvgIcon';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback } from 'react';
import { matchPath, useLocation } from 'react-router';

import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { leftNavigationBarTranslation } from '@/assets/locales/menu/leftNavigationBar.translation.pl.ts';
import { TOP_BAR_HEIGHT, TOP_BAR_MARGIN } from '@/components/layout/topBar/TopBar.styles.ts';
import { LeftBarNavigationBar } from '@/components/ui/leftBarNavigation/components/LeftBarNavigationBar.comp.tsx';
import {
  MENU_DIVIDER,
  MENU_LAYOUT,
  MenuRoute,
} from '@/components/ui/leftBarNavigation/menuLayout.model.ts';
import { SIDE_MENU_WIDTH } from '@/config/theme/theme.ts';
import { themeColors } from '@/config/theme/themeColors.ts';
import { AnnouncementAnchorKey } from '@/contexts/announcements/announcements.definitions.ts';
import { RouteEnum } from '@/models/route/Route.model.ts';
import { useVisibilityAwareInterval } from '@/utils/useVisibilityAwareInterval.util.ts';
import { useCarsToDo } from '@/views/cars/maintenance/util/useCarsToDo.util.ts';
import { catalogSectionOf } from '@/views/catalog/util/catalogSections.util.ts';
import { useReadChatConversationsStatus } from '@/views/chats/utils/useReadChatConversationsStatus.util.tsx';
import { useSettlementsToDo } from '@/views/settlements/util/useSettlementsToDo.util.ts';

const currentViews = Object.keys(leftNavigationBarTranslation) as RouteEnum[];

const MENU_ICONS: Record<MenuRoute, OverridableComponent<SvgIconTypeMap>> = {
  [RouteEnum.ADMINISTRATION]: AdminPanelSettingsOutlinedIcon,
  [RouteEnum.CALENDAR]: CalendarMonthOutlinedIcon,
  [RouteEnum.CARS]: DirectionsCarFilledOutlinedIcon,
  [RouteEnum.CATALOG]: RestaurantMenuOutlinedIcon,
  [RouteEnum.CHATS]: ChatOutlinedIcon,
  [RouteEnum.CONTACTS]: PermContactCalendarOutlinedIcon,
  [RouteEnum.CUSTOMERS]: BusinessIcon,
  [RouteEnum.DASHBOARD]: HomeOutlinedIcon,
  [RouteEnum.DICTIONARIES]: MenuBookIcon,
  [RouteEnum.EMAILS]: EmailOutlinedIcon,
  [RouteEnum.EMERGENCY_CONTACTS]: PhoneOutlined,
  [RouteEnum.HR]: BadgeOutlinedIcon,
  [RouteEnum.OFFERS]: LocalOfferOutlinedIcon,
  [RouteEnum.PROCESSES]: PointOfSaleOutlinedIcon,
  [RouteEnum.REPORTS]: AssessmentOutlinedIcon,
  [RouteEnum.SETTLEMENTS]: RequestQuoteOutlinedIcon,
  [RouteEnum.STATISTICS]: InsightsOutlinedIcon,
  [RouteEnum.SUPPLIERS]: LocalShippingOutlined,
  [RouteEnum.TASKS]: TaskAltIcon,
  [RouteEnum.USERS]: PersonOutlineIcon,
};

const MENU_ANCHORS: Partial<Record<MenuRoute, string>> = {
  [RouteEnum.CONTACTS]: AnnouncementAnchorKey.CONTACTS_MENU_ITEM,
  [RouteEnum.CUSTOMERS]: AnnouncementAnchorKey.CUSTOMERS_INDIVIDUAL_DISTINGUISHING_LABEL,
  [RouteEnum.TASKS]: AnnouncementAnchorKey.TASKS_MENU_ITEM,
};

export const LeftBarNavigation: React.FC = () => {
  const { pathname } = useLocation();
  const { chatConversationsStatus } = useReadChatConversationsStatus();
  const unreadCount = chatConversationsStatus.reduce((acc, it) => acc + it.unreadCount, 0);
  const { toDoCount } = useSettlementsToDo();
  const { toDoCount: carsToDoCount } = useCarsToDo();
  const queryClient = useQueryClient();
  const badgeCounts: Partial<Record<MenuRoute, number>> = {
    [RouteEnum.CARS]: carsToDoCount,
    [RouteEnum.CHATS]: unreadCount,
    [RouteEnum.SETTLEMENTS]: toDoCount,
  };
  const value = catalogSectionOf(pathname)
    ? RouteEnum.CATALOG
    : ([...currentViews]
        .sort((a, b) => b.length - a.length)
        .find(route => matchPath({ end: false, path: route }, pathname)) ?? RouteEnum.DASHBOARD);

  const refreshChatStatus = useCallback(() => {
    void queryClient.refetchQueries({ queryKey: [QueryKeyEnum.CHATS.ALL_STATUS] });
  }, [queryClient]);

  useVisibilityAwareInterval(refreshChatStatus, 60 * 1000);

  return (
    <Tabs
      orientation="vertical"
      sx={{
        '& .MuiButtonBase-root': { '&.MuiTabScrollButton-root': { height: '30px' } },
        borderRight: `1px solid ${themeColors.stroke}`,
        height: `calc(100vh - ${TOP_BAR_HEIGHT} - ${TOP_BAR_MARGIN})`,
        minWidth: SIDE_MENU_WIDTH,
      }}
      value={value}
      variant="scrollable"
    >
      {MENU_LAYOUT.map(entry =>
        entry === MENU_DIVIDER ? (
          <Tab
            disabled
            key={entry}
            sx={{
              borderTop: `2px solid ${themeColors.greyLight}`,
              height: 0,
              minHeight: 0,
              p: 0,
            }}
          />
        ) : (
          <LeftBarNavigationBar
            anchorKey={MENU_ANCHORS[entry]}
            badgeCount={badgeCounts[entry]}
            Icon={MENU_ICONS[entry]}
            isRed={entry === RouteEnum.EMERGENCY_CONTACTS}
            key={entry}
            selectedValue={entry === RouteEnum.EMERGENCY_CONTACTS ? value : undefined}
            value={entry}
          />
        ),
      )}
    </Tabs>
  );
};
